<template>
  <v-container fluid class="ma-0 pa-0">
    <v-card
      v-if="currentFields.length > 0"
      flat
      tile
      class="mx-4 my-0">
      <v-card-title v-if="title">
        <span style="font-size: 18px">{{ title }}</span>
      </v-card-title>
      <v-card-text :style="!title ? 'padding-top: 10px' : null">
        <v-form ref="form" lazy-validation>
        <v-row>
          <v-col
            v-if="fields === 'details'"
            cols="6"
            style="max-width: 700px; padding-top: 10px; padding-bottom: 10px">
            <v-text-field
              v-model="value.description"
              label="Description"
              :rules="[rules.required]"
              :readonly="!hasWriteAccess"
              required
              outlined
              dense
              hide-details="auto"
              validate-on-blur
              persistent-hint
              :hint="hasWriteAccess ? '*required' : null"
            />
          </v-col>
          <v-col
            style="padding-top: 10px; padding-bottom: 10px"
            v-for="(field, ind) in currentFields"
            :key="ind"
            cols="auto">
            <v-autocomplete
              v-if="field.value === 'source'"
              v-model="value.source"
              label="Source"
              :items="itemSources"
              item-text="display_name"
              item-value="source_id"
              :rules="[rules.required]"
              :readonly="!hasWriteAccess"
              required
              outlined
              dense
              persistent-hint
              :hint="hasWriteAccess ? '*required' : null"
              hide-details="auto"
              validate-on-blur>
            </v-autocomplete>
            <template 
              v-else-if="field.meta === true && field.value ===  'allergen'">
              <v-row>
                <v-col>
                  <v-text-field
                    v-model="value.metadata['allergen']['A']"
                    :label="field.key + '-always'"
                    :disabled="field.disabled || false"
                    :readonly="!hasWriteAccess"
                    outlined
                    dense
                    hide-details="auto">
                  </v-text-field>
                </v-col>
                <v-col>
                  <v-text-field
                    v-model="value.metadata['allergen']['W']"
                    :label="field.key + '-warning'"
                    :disabled="field.disabled || false"
                    :readonly="!hasWriteAccess"
                    outlined
                    dense
                    hide-details="auto">
                  </v-text-field>
                </v-col>
              </v-row>
            </template>
            <v-text-field
              v-else-if="field.meta === true"
              v-model="value.metadata[`${field.value}`]"
              :label="field.key"
              :disabled="field.disabled || false"
              :readonly="!hasWriteAccess"
              outlined
              dense
              hide-details="auto">
            </v-text-field>
            <v-text-field
              v-else-if="field.derived === true"
              v-model="value.metadata.derived[`${field.value}`]"
              :label="field.key"
              :disabled="field.disabled || false"
              :readonly="!hasWriteAccess"
              outlined
              dense
              hide-details="auto">
            </v-text-field>
            <v-text-field
              v-else-if="field.tenant_meta === true"
              v-model="value.metadata[tenantMetadataKey[$auth.tenant]][`${field.value}`]"
              :label="field.key"
              :disabled="field.disabled || false"
              :readonly="!hasWriteAccess"
              outlined
              dense
              hide-details="auto">
            </v-text-field>
            <v-text-field
              v-else-if="field.value === 'upc' || field.value === 'plu'"
              v-model="value[`${field.value}`]"
              @blur="handleBlur"
              :ref="field.value"
              :label="field.key"
              @keyup="handleKeyUp(field.value)"
              :disabled="field.disabled || false"
              :readonly="!hasWriteAccess"
              :rules="[validUpcOrPlu]"
              outlined
              dense
              hide-details="auto"
              :error-messages="field.value === 'upc' && (upcRes && !upcRes.original_is_valid)
                  ? `${upcRes.message}`
                  : []"
              persistent-hint
              :hint="hasWriteAccess ? '*Either UPC or PLU is required' : null"
              validate-on-blur>
              <template v-slot:message>
                <span v-if="field.value === 'upc' && (upcRes &&
                    !upcRes.original_is_valid &&
                    upcRes.standardized_upc)"
                  style="display: inline-block">
                  {{ upcRes.message }}.
                  <span style="color: #333">
                    Did you mean:
                    <span style="font-weight: 500">
                      {{ upcRes.standardized_upc }}?
                    </span>
                  </span>
                  <v-btn
                    x-small
                    color="success"
                    outlined
                    @click="updateUpc(upcRes.standardized_upc)">
                    Yes
                  </v-btn>
                </span>
              </template>
            </v-text-field>
            <v-text-field
              v-else-if="field.value === 'shipping_cube' || field.value === 'shipping_unit_weight'"
              :value="parseFloat(value.metadata[`${field.value}`]).toFixed(2)"
              :type="field.hasOwnProperty('type') ? field.type : 'text'"
              :label="field.key"
              :disabled="field.disabled || false"
              :rules="field.required ? [rules.required] : []"
              :required="field.required || false"
              :readonly="!hasWriteAccess"
              outlined
              dense
              hide-details="auto"
              validate-on-blur
              :prefix="field.prefix || null">
            </v-text-field>
            <v-text-field
              v-else-if="field.value ===  'balance_on_hand_qty'"
              v-model="value.metadata[`${field.value}`]"
              :type="field.hasOwnProperty('type') ? field.type : 'text'"
              :label="field.key"
              :disabled="field.disabled || false"
              :rules="field.required ? [rules.required] : []"
              :required="field.required || false"
              :readonly="!hasWriteAccess"
              outlined
              dense
              hide-details="auto"
              validate-on-blur
              :prefix="field.prefix || null">
            </v-text-field>
            <v-autocomplete
              v-else-if="field.value === 'status' && showStatusDropDown"
              v-model="value.status"
              :label="field.key"
              :items="statusList"
              :readonly="!hasWriteAccess"
              outlined
              dense
              validate-on-blur>
            </v-autocomplete>
            <v-text-field
              v-else
              v-model="value[`${field.value}`]"
              :type="field.hasOwnProperty('type') ? field.type : 'text'"
              :label="field.key"
              :disabled="field.disabled || false"
              :rules="field.required ? [rules.required] : []"
              :required="field.required || false"
              :readonly="!hasWriteAccess"
              outlined
              dense
              hide-details="auto"
              persistent-hint
              :hint="(field.value === 'item_id' && hasWriteAccess) ? '*required' : ''"
              validate-on-blur
              :prefix="field.prefix || null">
            </v-text-field>
          </v-col>
        </v-row>
        </v-form>
      </v-card-text>
    </v-card>
  </v-container>
</template>
<script>
import { cloneDeep, has } from "lodash"
import { root } from "@/mixins/root"
import { retailerHiddenFields } from '@/components/items/data/metadataFields'
export default {
  data () {
    return {
      rules: {
        required: value => !!value || "This field is required"
      },
      childUpcRes: {},
      formHasErrors: false,
      details: [
        { key: "Source", value: "source", required: true },
        { key: "Barcode", value: "barcode" },
        { key: "UPC", value: "upc", required: true },
        { key: "PLU", value: "plu", required: true },
        {
          key: "Quantity Per Case",
          value: "qty_per_case",
          required: this.$auth.tenant === "awg",
          type: "number"
        },
        { key: "Pack Size", value: "pack_size" },
        { key: this.$auth.tenant === 'jbg' ? 'SRP' : 'Sale Price', value: "sale_price", prefix: "$", type: "number" },
        { key: "Cost", value: "cost", prefix: "$", type: "number" },
        { key: "Vendor Id", value: "vendor_id", retailerRestricted: this.$auth.tenant === 'jbg' },
        { key: "Vendor Name", value: "vendor_name", retailerRestricted: this.$auth.tenant === 'jbg' },
        { key: "Status", value: "status" },
        {
          key: "Department ID",
          value: "department_id",
          required: this.$auth.tenant === "awg"
        }
      ],
      taxonomy: [
        { key: "Department", value: "department", order: 0, show: true },
        { key: "Category", value: "category", order: 1, show: true },
        { key: "Subcategory", value: "subcategory", order: 2, show: true },
        { key: "Weight", value: "shipping_unit_weight", order: 3, type: "decimal", show: this.$auth.tenant === 'jbg' },
        { key: "Cube", value: "shipping_cube", order: 4, type: "decimal", show: this.$auth.tenant === 'jbg' },
        { key: "On Hand Quantity", value: "balance_on_hand_qty", order: 5, type: "number", show: this.$auth.tenant === 'jbg' }
      ],
      tenantMetadataKey: {
        pricechopper: 'pce_metadata' 
      },
      statusList: ["DISCONTINUED ITEM", "Active"]
    }
  },
  name: "ItemData",
  mixins: [root],
  props: ["value", "title", "fields", "crud", "upcRes", "hideEmpty"],
  watch: {
    value: {
      handler (newValue) {
        if (newValue) {
          if (newValue.metadata && newValue.metadata.case_pack) {
            newValue.case_pack = newValue.metadata.case_pack
          }
        }
      },
      deep: true
    }
  },
  created () {
    this.childUpcRes = JSON.parse(JSON.stringify(this.upcRes))
    if (this.value && this.value.metadata && this.value.metadata.case_pack) {
      this.value.case_pack = this.value.metadata.case_pack
    }
  },
  computed: {
    currentFields () {
      if (this.formFields.length > 0) {
        return this.crud === "edit" && this.hideEmpty
          ? this.nonEmptyFields
          : this.formFields
      }
      return []
    },
    nonEmptyFields () {
      if (this.formFields.length > 0) {
        return this.formFields.filter((field) => {
          const key = field.value
          let value = this.value[key]
          if (this.fields === "metadata") {
            value = this.value.metadata[key]
          } else if (this.fields === "tenant_metadata") {
            value = this.value.metadata[this.tenantMetadataKey[this.$auth.tenant]][key]
          } else if (this.fields === "derived") {
            value = this.value.metadata.derived[key]
          } else if (this.fields === "taxonomy") {
            value = this.value[key] || this.value.metadata[key]
          }
          if (value !== null && value !== undefined && value !== "") {
            return field
          }
        })
      }
      return []
    },
    formFields () {
      switch (this.fields) {
        case "details":
          return [
            {
              key: "Item Id",
              value: "item_id",
              disabled: this.crud === "edit",
              required: true
            },
            ...this.details.filter(field => {
              if (['cost', 'sale_price'].includes(field.value)) {
                return this.showPriceFields
              }
              return !(field.retailerRestricted && this.isRetailer)
            })
          ]
        case "taxonomy":
          return this.taxonomy.filter(t => t.show)
        case "metadata":
          return this.metadata
        case "derived":
          return this.derived
        case "tenant_metadata":
          return this.tenantMetadata
        default:
          return []
      }
    },
    itemSources () {
      return this.$store.getters.sources
    },
    metadata () {
      if (this.value && has(this.value, "metadata") && this.value.metadata) {
        let meta = cloneDeep(this.value.metadata)
        if (['jbg'].includes(this.$auth.tenant)) {
          meta = this.filterMetadata(meta)
        }
        const fields = Object.keys(meta).flatMap((key) => {
          const excludedKeys = ['derived', 'case_pack', this.tenantMetadataKey[this.$auth.tenant]].concat(this.$auth.tenant === 'jbg' ? ['shipping_unit_weight', 'shipping_cube', 'balance_on_hand_qty'] : [])

          if (!excludedKeys.includes(key)) {
            return {
              key: key.replace(/_/g, " "),
              value: key,
              empty: !meta[key],
              meta: true
            }
          }
          return []
        })
        return this.sortByKey(fields, "key")
      } else {
        return []
      }
    },
    tenantMetadata () {
      if (this.value && has(this.value, "metadata") && this.tenantMetadataKey[this.$auth.tenant] && this.value.metadata[this.tenantMetadataKey[this.$auth.tenant]]) {
        let pce_meta = cloneDeep(this.value.metadata.pce_metadata)
        const fields = Object.keys(pce_meta).flatMap((key) => {
          return {
            key: key.replace(/_/g, " "),
            value: key,
            empty: !pce_meta[key],
            tenant_meta: true
          }
        })
        return this.sortByKey(fields, "key")
      } else {
        return []
      }
    },
    derived () {
      if (this.value && has(this.value, "metadata.derived")) {
        let derived = cloneDeep(this.value.metadata.derived)
        const fields = Object.keys(derived).map((key) => {
          return {
            key: key.replace(/_/g, " "),
            value: key,
            empty: !derived[key],
            derived: true
          }
        })
        return this.sortByKey(fields, "key")
      } else {
        return []
      }
    },
    upcPluError () {
      return (!this.value.upc && !this.value.plu)
    },
    showPriceFields() {
      if (this.$auth.tenant === 'jbg' && this.isRetailer) {
        return (this.value && this.value.sale_price != 0)
      }
      return true
    },
    showStatusDropDown() {
      return this.$auth.tenant === 'alliance-retail-group' && isNaN(this.value.item_id.charAt(0))
    }
  },
  methods: {
    validUpcOrPlu (value) {
      if (this.upcPluError) {
        return !!value || 'UPC or PLU is required'
      }
      return true
    },
    validate (fieldRef) {
      return this.$refs[fieldRef][0].validate(true)
    },
    handleKeyUp (fieldValue) {
      const fieldToValidate = fieldValue === 'upc' ? 'plu' : 'upc'
      return this.validate(fieldToValidate)
    },
    handleBlur () {
      // always validate both on blur
      this.validate('upc')
      this.validate('plu')
      if (!this.upcPluError) {
        this.removeUpcVerify()
      }
    },
    removeUpcVerify () {
      // if UPC verify appears and a user no longer wants a upc, remove the verify
      // if the UPC field is empty and loses focus.
      if (!this.value.upc || this.value.upc.length === 0) {
        this.$emit('resetUpcRes')
      }
    },
    resetForm () {
      this.$refs.form.reset()
    },
    submit () {
      const valid = this.$refs.form.validate()
      return (!this.upcPluError && valid)
    },
    updateUpc (upc) {
      this.$emit("upc", upc)
    },
    sortByKey (arr, key) {
      if (arr.length > 1) {
        arr = arr
          .map((obj) => obj[key])
          .sort()
          .map((v) => arr.find((obj) => obj[key] === v))
      }
      return arr
    },
    filterMetadata(metadata) {
      let trimmed = cloneDeep(metadata)
      if (this.isRetailer) {
        trimmed = Object.keys(metadata).filter(key => retailerHiddenFields[this.$auth.tenant] && !retailerHiddenFields[this.$auth.tenant].includes(key)).reduce((obj, key) => {
          obj[key] = metadata[key]
          return obj
        }, {})
        if (this.$auth.tenant === 'jbg') {
          const hasPMCStore = this.userStores.some(store => store.attributes && store.attributes.STORE_PRICE_LIST && store.attributes.STORE_PRICE_LIST.toLowerCase().includes('pmc'))
          if (!hasPMCStore) {
            delete trimmed.cost_allowance
          }
        }
      }
      return trimmed
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/items/ItemData.vue